import { IngestionClient, DataGeneratorType } from '@phonepe/conduit';
import {
	FOXTROT_DEFAULT_CONFIG,
	FOXTROT_DEFAULT_EVENT_DATA,
	X_DEVICE_FINGERPRINT,
	X_SOURCE_VERSION,
} from '../constants';
import {
	BUILD_ENVIRONMENT,
	IBaseEvent,
	AnalyticsClientName,
	TAnalyticsEventSchema,
	IAnalyticsConfig,
	FoxtrotClient,
	IGarfieldConfig,
	IAnalyticsBaseConfig,
	IFoxtrotConfig,
	TFlushQueuedEventHandler,
} from '../types';

export default class AnalyticsClient {
	private _foxtrotClient?: FoxtrotClient;
	private _garfieldClient?: typeof window.garfield;
	private readonly _config: IAnalyticsConfig = {};
	private _queuedEvents: Array<TAnalyticsEventSchema> = [];
	constructor({
		environment = BUILD_ENVIRONMENT.development,
	}: IAnalyticsBaseConfig) {
		this._config = {
			environment,
		};
	}

	initGarfield(config: IGarfieldConfig) {
		this._config.garfield = config;
		const { trackerId } = this._config.garfield;
		if (window.garfield) {
			window.garfield('create', { id: trackerId });
			this._garfieldClient = window.garfield.bind(window);
		}
		this._updateGarfieldAuthFlow();
	}

	initFoxtrot(config: IFoxtrotConfig) {
		this._config.foxtrot = config;
		const {
			flushInterval: defaultFlushInterval,
			schemaName,
			sensitivity,
		} = FOXTROT_DEFAULT_CONFIG;

		const { customIngestionHandler, flushInterval = defaultFlushInterval } =
			this._config.foxtrot;

		const client = new IngestionClient({
			environment:
				this._config.environment === BUILD_ENVIRONMENT.production
					? 'production'
					: 'stage',
			flushInterval,
			generators: [
				{
					type: DataGeneratorType.FOXTROT,
					options: {
						schemaName: schemaName,
						sensitivity: sensitivity,
					},
				},
			],
			customAxiosFn: customIngestionHandler,
		});
		this._foxtrotClient = client.getClients().foxtrot;
	}

	updateGarfieldConfig(config: Partial<IGarfieldConfig> = {}) {
		if (!this._garfieldClient) {
			console.warn('Garfield not initialized...');
			return;
		}
		this._config.garfield = { ...this._config.garfield!, ...config };

		this._updateGarfieldAuthFlow();
	}

	sendFoxtrotEvent(eventSchema: TAnalyticsEventSchema) {
		if (!this._config.foxtrot) return;

		const { app } = this._config.foxtrot;
		const finalFoxtrotEvent = {
			...FOXTROT_DEFAULT_EVENT_DATA,
			app,
			...eventSchema,
		};

		this._dispatchEvent(
			AnalyticsClientName.FOXTROT,
			finalFoxtrotEvent,
			(finalEventData) => {
				this._foxtrotClient?.ingest(finalEventData);
			},
		);
	}

	sendGarfieldEvent(eventSchema: TAnalyticsEventSchema) {
		if (!this._config.garfield) return;

		const { handleGarfieldEventData } = this._config.garfield;
		const { eventData, eventType } = eventSchema;
		const { name, value = 'NA', additionalData } = eventData;

		const obj = {
			eventType,
			eventName: name,
			value,
			additionalData,
		};

		const finalGarfieldEvent = handleGarfieldEventData?.(obj) ?? obj;

		this._dispatchEvent(
			AnalyticsClientName.GARFIELD,
			finalGarfieldEvent,
			(finalEventData) => {
				this._garfieldClient?.('send', finalEventData);
			},
		);
	}

	/**
	 * @description Pushed the event to a temporary queue used to capture the events before user's auth
	 * @param {TAnalyticsEventSchema} data
	 */
	pushEventToQueue(data: TAnalyticsEventSchema) {
		this._queuedEvents.push(data);
	}

	flushQueuedEvent(handler: TFlushQueuedEventHandler) {
		if (this._queuedEvents.length !== 0) {
			this._queuedEvents.forEach((eventSchema: TAnalyticsEventSchema) => {
				const normalizedData = handler(eventSchema);
				this.sendFoxtrotEvent(normalizedData);
			});
			this._queuedEvents = [];
		}
	}

	flush() {
		this._foxtrotClient?.flush();
	}

	private _updateGarfieldAuthFlow() {
		/**
		 * Used for authenticated flows updating the garfield config once the user logs in to the dashboard
		 */
		const { trackerId, authFlow } = this._config.garfield ?? {};
		if (authFlow) {
			this._garfieldClient?.('set', {
				id: trackerId,
				headers: {
					[X_DEVICE_FINGERPRINT]: authFlow.fingerprint,
					[X_SOURCE_VERSION]: authFlow.version,
				},
				userFlow: authFlow.isLoggedIn ? 'AUTH' : '',
			});
		}
	}

	private _dispatchEvent<T extends IBaseEvent>(
		client: AnalyticsClientName,
		finalEventData: T,
		eventDispatcher: (finalEventData: T) => void,
	) {
		const { environment } = this._config;
		const { production, staging } = BUILD_ENVIRONMENT;

		if ([production, staging].includes(environment!)) {
			eventDispatcher(finalEventData);
			return;
		}

		const { eventType, ...eventData } = finalEventData;
		console.log(`${client} ${eventType} Event - \n`, eventData);
	}
}
